import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { format, startOfDay } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar as CalendarIcon, Check, X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function CustomerAppointment() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const token = searchParams.get('token');
  const action = (searchParams.get('action') as 'cancel' | 'reschedule' | 'book') || 'book'; // Default to 'book' if no action specified
  
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [appointmentData, setAppointmentData] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [cancelReason, setCancelReason] = useState('');
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    console.log('🔍 [CustomerAppointment] Starting token verification');
    console.log('🔍 [CustomerAppointment] Token:', token);
    console.log('🔍 [CustomerAppointment] Action:', action);
    
    try {
      console.log('📡 [CustomerAppointment] Invoking verify-appointment-token function');
      const { data, error } = await supabase.functions.invoke('verify-appointment-token', {
        body: { token }
      });

      console.log('📥 [CustomerAppointment] Function response received');
      console.log('📊 [CustomerAppointment] Data:', JSON.stringify(data, null, 2));
      console.log('❌ [CustomerAppointment] Error:', error);

      if (error) {
        console.error('❌ [CustomerAppointment] Edge function error:', error);
        toast({
          title: "Fehler",
          description: "Link konnte nicht überprüft werden: " + (error.message || 'Unbekannter Fehler'),
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      if (!data?.valid) {
        console.log('❌ [CustomerAppointment] Token validation failed');
        toast({
          title: "Ungültiger Link",
          description: "Dieser Link ist abgelaufen oder ungültig.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      console.log('✅ [CustomerAppointment] Token verified successfully, setting appointment data');
      setAppointmentData(data);
      setLoading(false);
      console.log('✅ [CustomerAppointment] Loading set to false');
    } catch (error: any) {
      console.error('💥 [CustomerAppointment] Exception in verifyToken:', error);
      console.error('💥 [CustomerAppointment] Error stack:', error.stack);
      toast({
        title: "Fehler",
        description: "Link konnte nicht überprüft werden: " + (error.message || 'Unbekannter Fehler'),
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const generateTimeSlots = () => {
    const slots: string[] = [];
    const isSaturday = selectedDate && selectedDate.getDay() === 6;
    
    if (isSaturday) {
      for (let hour = 10; hour <= 16; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          if (hour === 16 && minute > 15) break;
          slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
        }
      }
    } else {
      for (let hour = 10; hour <= 13; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          if (hour === 13 && minute > 0) break;
          slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
        }
      }
      
      for (let hour = 15; hour <= 19; hour++) {
        const startMinute = hour === 15 ? 30 : 0;
        for (let minute = startMinute; minute < 60; minute += 15) {
          if (hour === 19 && minute > 30) break;
          slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
        }
      }
    }
    
    return slots;
  };

  const handleCancel = async () => {
    if (!token) return;
    
    setVerifying(true);
    try {
      const { error } = await supabase.functions.invoke('customer-manage-appointment', {
        body: {
          token,
          action: 'cancel',
          reason: cancelReason
        }
      });

      if (error) throw error;

      toast({
        title: "Termin abgesagt",
        description: "Sie und Ihr Berater wurden per E-Mail informiert.",
      });
      setCompleted(true);
    } catch (error) {
      console.error('Error canceling appointment:', error);
      toast({
        title: "Fehler",
        description: "Termin konnte nicht abgesagt werden.",
        variant: "destructive"
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleReschedule = async () => {
    if (!token || !selectedDate) return;
    
    setVerifying(true);
    try {
      const [hours, minutes] = selectedTime.split(':');
      const newDate = new Date(selectedDate);
      newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

      const { error } = await supabase.functions.invoke('customer-manage-appointment', {
        body: {
          token,
          action: 'reschedule',
          new_date: newDate.toISOString()
        }
      });

      if (error) throw error;

      toast({
        title: "Termin verschoben",
        description: "Ihr Berater wurde über die Änderung informiert.",
      });
      setCompleted(true);
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast({
        title: "Fehler",
        description: "Termin konnte nicht verschoben werden.",
        variant: "destructive"
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleBook = async () => {
    if (!token || !selectedDate) return;
    
    setVerifying(true);
    try {
      const [hours, minutes] = selectedTime.split(':');
      const newDate = new Date(selectedDate);
      newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

      const { error } = await supabase.functions.invoke('customer-manage-appointment', {
        body: {
          token,
          action: 'book',
          new_date: newDate.toISOString()
        }
      });

      if (error) throw error;

      toast({
        title: "Termin gebucht",
        description: "Sie und Ihr Berater erhalten eine Bestätigung per E-Mail.",
      });
      setCompleted(true);
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast({
        title: "Fehler",
        description: "Termin konnte nicht gebucht werden.",
        variant: "destructive"
      });
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Überprüfe Link...</p>
        </Card>
      </div>
    );
  }

  if (!token || !appointmentData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <X className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Ungültiger Link</h1>
          <p className="text-muted-foreground">
            Dieser Link ist abgelaufen oder ungültig. Bitte kontaktieren Sie Ihren Berater.
          </p>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">
            {action === 'cancel' ? 'Termin abgesagt' : action === 'book' ? 'Termin gebucht' : 'Termin verschoben'}
          </h1>
          <p className="text-muted-foreground">
            {action === 'cancel' 
              ? 'Sie und Ihr Berater wurden per E-Mail informiert.'
              : action === 'book'
              ? 'Sie und Ihr Berater erhalten eine Bestätigung per E-Mail.'
              : 'Ihr Berater wurde über die Änderung informiert und wird Ihnen eine Bestätigung zusenden.'}
          </p>
        </Card>
      </div>
    );
  }

  const currentDate = appointmentData.appointment_date ? new Date(appointmentData.appointment_date) : null;

  console.log('🎨 Rendering appointment page:', {
    action,
    hasAppointmentDate: !!appointmentData.appointment_date,
    currentDate,
    appointmentData
  });

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-2xl mx-auto">
        <Card className="p-6 sm:p-8">
          <div className="mb-6 text-center">
            {appointmentData.organization_logo && (
              <img 
                src={appointmentData.organization_logo} 
                alt={appointmentData.organization_name || 'Logo'} 
                className="h-16 w-auto mx-auto mb-4"
              />
            )}
            {appointmentData.organization_name && (
              <p className="text-lg font-semibold mb-4">
                {appointmentData.organization_name}
              </p>
            )}
            <h1 className="text-2xl font-bold flex items-center justify-center gap-2 mb-2">
              <CalendarIcon className="h-6 w-6 text-primary" />
              {action === 'book' ? 'Termin buchen' : action === 'cancel' ? 'Termin absagen' : 'Termin verschieben'}
            </h1>
            {action !== 'book' && currentDate && (
              <p className="text-muted-foreground">
                Aktueller Termin: {format(currentDate, 'EEEE, dd. MMMM yyyy - HH:mm', { locale: de })} Uhr
              </p>
            )}
            {action === 'book' && (
              <p className="text-muted-foreground">
                Wählen Sie einen passenden Termin für Ihr Beratungsgespräch.
              </p>
            )}
            <p className="text-sm text-muted-foreground mt-2">
              Berater: {appointmentData.advisor_name}
            </p>
          </div>

          {action === 'cancel' && (
            <div className="space-y-4">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <p className="font-medium text-sm mb-2">Termin absagen</p>
                <p className="text-sm text-muted-foreground">
                  Sie und Ihr Berater erhalten eine Absage-Benachrichtigung per E-Mail.
                </p>
              </div>

              <div>
                <Label htmlFor="reason">Grund der Absage (optional)</Label>
                <Textarea
                  id="reason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Z.B. Terminkonflikt, anderer Zeitpunkt gewünscht..."
                  className="mt-2 min-h-[100px]"
                />
              </div>

              <Button
                onClick={handleCancel}
                disabled={verifying}
                variant="destructive"
                className="w-full"
              >
                {verifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird abgesagt...
                  </>
                ) : (
                  'Termin absagen'
                )}
              </Button>
            </div>
          )}

          {(action === 'reschedule' || action === 'book') && (
            <div className="space-y-6">
              <div>
                <Label className="text-base font-semibold mb-3 block">
                  {action === 'book' ? 'Datum wählen' : 'Neues Datum wählen'}
                </Label>
                <div className="bg-card rounded-lg border p-2 flex justify-center">
                  <Calendar 
                    mode="single" 
                    selected={selectedDate} 
                    onSelect={setSelectedDate}
                    disabled={(date) => date < startOfDay(new Date())}
                    initialFocus 
                    locale={de}
                    className="pointer-events-auto"
                  />
                </div>
              </div>

              {selectedDate && (
                <div>
                  <Label className="text-base font-semibold mb-3 block">Zeit wählen</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {generateTimeSlots().map((time) => (
                      <Button
                        key={time}
                        variant={selectedTime === time ? 'default' : 'outline'}
                        onClick={() => setSelectedTime(time)}
                        className="text-sm"
                      >
                        {time}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={action === 'book' ? handleBook : handleReschedule}
                disabled={!selectedDate || verifying}
                className="w-full"
              >
                {verifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {action === 'book' ? 'Wird gebucht...' : 'Wird verschoben...'}
                  </>
                ) : (
                  action === 'book' ? 'Termin buchen' : 'Termin verschieben'
                )}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
